import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { RadioGroup } from '@roger/ui-components';
import {
  BespokeRadioRowProvider,
  useBespokeSectionContext,
} from '../../primitives';
import { useQuestionFieldDisabled } from '../../primitives/useQuestionFieldDisabled';
import type { RepeatBindings } from '../../primitives';
import {
  joinClasses,
  BESPOKE_TABLE_BODY_ROW_CLASS,
  BESPOKE_TABLE_CELL_CLASS,
  BESPOKE_TABLE_COL_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_CELL_CLASS,
  BESPOKE_TABLE_COMPACT_COL_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_ROW_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_ROW_LABEL_CLASS,
  BESPOKE_TABLE_DENSITY_COMPACT_PAD_CLASS,
  BESPOKE_TABLE_DENSITY_DEFAULT_PAD_CLASS,
  BESPOKE_TABLE_DENSITY_LOOSE_PAD_CLASS,
  BESPOKE_TABLE_ELEMENT_CLASS,
  BESPOKE_TABLE_ROW_HEADER_CLASS,
  BESPOKE_TABLE_ROW_LABEL_CLASS,
  BESPOKE_TABLE_SHELL_CLASS,
  BESPOKE_TABLE_SHELL_STRUCTURAL_CLASS,
  BESPOKE_TABLE_STRUCTURAL_CELL_CLASS,
  BESPOKE_TABLE_STRUCTURAL_COL_HEADER_CLASS,
  BESPOKE_TABLE_STRUCTURAL_ROW_HEADER_CLASS,
  BESPOKE_TABLE_STRUCTURAL_ROW_LABEL_CLASS,
} from '../../primitives/fieldClassSlots';
import type { FormValue } from '../../../types';
import { QidTag } from '../../../../dev/QidTag';
import { useLayoutDerived } from '../LayoutDerivedContext';
import { layoutInlineStyle } from '../layoutInlineStyle';
import type { LayoutNodeChildProps } from '../nodeRenderProps';
import type {
  LayoutInlineStyle,
  LayoutNode,
  TableChromeMode,
  TableNode,
  TableVariant,
} from '../types';
import { conditionalWhenSatisfied } from '../whenClause';

type TableRowNode = TableNode['rows'][number];

type TableSlotChrome = {
  shell: string;
  rowHeader: string;
  colHeader: string;
  rowLabel: string;
  cell: string;
  bodyRow: string | undefined;
};

function densityPad(variant: TableVariant): string {
  switch (variant) {
    case 'compact':
      return BESPOKE_TABLE_DENSITY_COMPACT_PAD_CLASS;
    case 'loose':
      return BESPOKE_TABLE_DENSITY_LOOSE_PAD_CLASS;
    default:
      return BESPOKE_TABLE_DENSITY_DEFAULT_PAD_CLASS;
  }
}

/** @deprecated `chrome` defaults to `legacy` for back-compat; prefer `"none"`. */
function resolveChromeMode(
  chrome: TableChromeMode | undefined
): TableChromeMode {
  return chrome ?? 'legacy';
}

function resolveVariant(variant: TableVariant | undefined): TableVariant {
  return variant ?? 'default';
}

/**
 * Built-in slot classes. `chrome` is deprecated — `legacy` keeps historical
 * primary-wash packs; `none` is structural + density only so author
 * `*ClassName` / `*Style` own the look (same model as `stack` / `card` / `box`).
 */
function tableSlotChrome(
  variant: TableVariant,
  chrome: TableChromeMode
): TableSlotChrome {
  if (chrome === 'none') {
    const pad = densityPad(variant);
    return {
      shell: BESPOKE_TABLE_SHELL_STRUCTURAL_CLASS,
      rowHeader: joinClasses(BESPOKE_TABLE_STRUCTURAL_ROW_HEADER_CLASS, pad),
      colHeader: joinClasses(BESPOKE_TABLE_STRUCTURAL_COL_HEADER_CLASS, pad),
      rowLabel: joinClasses(BESPOKE_TABLE_STRUCTURAL_ROW_LABEL_CLASS, pad),
      cell: joinClasses(BESPOKE_TABLE_STRUCTURAL_CELL_CLASS, pad),
      bodyRow: undefined,
    };
  }

  // legacy — variant compact selects compact pack; default/loose → default pack
  if (variant === 'compact') {
    return {
      shell: BESPOKE_TABLE_SHELL_CLASS,
      rowHeader: BESPOKE_TABLE_COMPACT_ROW_HEADER_CLASS,
      colHeader: BESPOKE_TABLE_COMPACT_COL_HEADER_CLASS,
      rowLabel: BESPOKE_TABLE_COMPACT_ROW_LABEL_CLASS,
      cell: BESPOKE_TABLE_COMPACT_CELL_CLASS,
      bodyRow: BESPOKE_TABLE_BODY_ROW_CLASS,
    };
  }

  return {
    shell: BESPOKE_TABLE_SHELL_CLASS,
    rowHeader: BESPOKE_TABLE_ROW_HEADER_CLASS,
    colHeader: BESPOKE_TABLE_COL_HEADER_CLASS,
    rowLabel: BESPOKE_TABLE_ROW_LABEL_CLASS,
    cell: BESPOKE_TABLE_CELL_CLASS,
    bodyRow: BESPOKE_TABLE_BODY_ROW_CLASS,
  };
}

function mergeInlineStyles(
  ...parts: (LayoutInlineStyle | undefined)[]
): CSSProperties | undefined {
  const merged: LayoutInlineStyle = {};
  for (const part of parts) {
    if (part == null) continue;
    Object.assign(merged, part);
  }
  return layoutInlineStyle(Object.keys(merged).length > 0 ? merged : undefined);
}

type RenderLayoutNode = (props: {
  node: LayoutNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) => ReactNode;

const DIRECT_CELL_CLICK_TARGET_SELECTOR =
  'input, button, select, textarea, label, a, [role="button"], [role="checkbox"], [role="radio"], [data-no-cell-click]';

const CELL_CHOICE_CONTROL_SELECTOR =
  'input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled), [role="checkbox"]:not([aria-disabled="true"]):not(:disabled), [role="radio"]:not([aria-disabled="true"]):not(:disabled)';

function findRadioQidInCells(cells: LayoutNode[]): string | undefined {
  for (const cell of cells) {
    if (cell.type === 'radio-option') {
      return cell.qid;
    }
  }
  return undefined;
}

// When a cell has a single radio or checkbox, clicking anywhere in the cell selects it
// so users don't have to aim at the small control.
function handleChoiceTableCellClick(event: MouseEvent<HTMLTableCellElement>) {
  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  if (target.closest(DIRECT_CELL_CLICK_TARGET_SELECTOR)) {
    return;
  }

  const controls = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      CELL_CHOICE_CONTROL_SELECTOR
    )
  );

  if (controls.length !== 1) {
    return;
  }

  controls[0].click();
}

function TableRow({
  row,
  answers,
  repeatBindings,
  renderNode,
  tableNode,
  chrome,
}: {
  row: TableRowNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
  renderNode: RenderLayoutNode;
  tableNode: TableNode;
  chrome: TableSlotChrome;
}) {
  const { onAnswerChange, schemaMap } = useBespokeSectionContext();
  const radioQid = findRadioQidInCells(row.cells);
  const question = radioQid != null ? schemaMap.get(radioQid) : undefined;
  const rowRadioDisabled = useQuestionFieldDisabled(question);
  const selectedValue = radioQid != null ? String(answers[radioQid] ?? '') : '';

  const cells = row.cells.map((cell, ci) => (
    <td
      key={ci}
      className={joinClasses(
        chrome.cell,
        tableNode.cellClassName,
        row.cellClassName
      )}
      style={mergeInlineStyles(tableNode.cellStyle, row.cellStyle)}
      onClick={handleChoiceTableCellClick}
    >
      {renderNode({ node: cell, answers, repeatBindings })}
    </td>
  ));

  const tr = (
    <tr
      className={joinClasses(
        chrome.bodyRow,
        tableNode.rowClassName,
        row.rowClassName
      )}
      style={mergeInlineStyles(tableNode.rowStyle, row.rowStyle)}
    >
      <td
        className={joinClasses(
          chrome.rowLabel,
          tableNode.rowLabelClassName,
          row.rowLabelClassName
        )}
        style={mergeInlineStyles(tableNode.rowLabelStyle, row.rowLabelStyle)}
      >
        {row.label}
        {radioQid != null && <QidTag qid={radioQid} />}
      </td>
      {radioQid != null ? (
        <BespokeRadioRowProvider>{cells}</BespokeRadioRowProvider>
      ) : (
        cells
      )}
    </tr>
  );

  if (radioQid == null) {
    return tr;
  }

  return (
    <RadioGroup
      asChild
      className='table-row'
      value={selectedValue}
      disabled={rowRadioDisabled}
      onValueChange={v => {
        if (rowRadioDisabled || radioQid == null) return;
        onAnswerChange(radioQid, v);
      }}
    >
      {tr}
    </RadioGroup>
  );
}

export function Table({
  node,
  answers,
  repeatBindings,
  renderNode,
}: LayoutNodeChildProps<TableNode> & {
  renderNode: RenderLayoutNode;
}) {
  const { schemaMap } = useBespokeSectionContext();
  const derived = useLayoutDerived();
  const variant = resolveVariant(node.variant);
  const chromeMode = resolveChromeMode(node.chrome);
  const chrome = tableSlotChrome(variant, chromeMode);
  const visibleRows = node.rows
    .map((row, ri) => ({ row, ri }))
    .filter(({ row }) =>
      conditionalWhenSatisfied(row, answers, repeatBindings, derived, schemaMap)
    );

  return (
    <div
      className={joinClasses(chrome.shell, node.className)}
      style={layoutInlineStyle(node.style)}
    >
      <table
        className={joinClasses(
          BESPOKE_TABLE_ELEMENT_CLASS,
          node.tableClassName
        )}
        style={layoutInlineStyle(node.tableStyle)}
      >
        <thead>
          <tr>
            <th
              className={joinClasses(chrome.rowHeader, node.rowHeaderClassName)}
              style={layoutInlineStyle(node.rowHeaderStyle)}
            >
              {node.rowHeader ?? ''}
            </th>
            {node.colHeaders.map(h => (
              <th
                key={h}
                className={joinClasses(
                  chrome.colHeader,
                  node.colHeaderClassName
                )}
                style={layoutInlineStyle(node.colHeaderStyle)}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ row, ri }) => (
            <TableRow
              key={ri}
              row={row}
              answers={answers}
              repeatBindings={repeatBindings}
              renderNode={renderNode}
              tableNode={node}
              chrome={chrome}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
